import { isNil } from 'lodash';
import { z } from 'zod';
import { EmptyValidationMessage } from '@ninja/utilities';
import { PolicyType, SalaryBands, isEmirateCountry } from '../../../../../common';
import {
	useEidCommonFieldsStructure,
	useEidDependentsFieldsStructure,
	useEidDomesticWorkerFieldsStructure,
	useEidPrincipalFieldsStructure,
} from '../fields-structures';
import { IEidMembersForm } from '../types';

export const aggregateValidations = (
	data: IEidMembersForm,
	getCommonValidations: ReturnType<typeof useEidCommonFieldsStructure>['getValidations'],
	getPrincipalValidations: ReturnType<typeof useEidPrincipalFieldsStructure>['getValidations'],
	getDependentsValidations: ReturnType<typeof useEidDependentsFieldsStructure>['getValidations'],
	getDomesticWorkerValidations: ReturnType<typeof useEidDomesticWorkerFieldsStructure>['getValidations'],
	maximumNoOfMember: number
) => {
	const getBaseValidation = () =>
		z
			.object({
				policyTypeId: z.number({ required_error: EmptyValidationMessage }).refine(
					(policyTypeId) => {
						switch (policyTypeId) {
							case PolicyType.OnlyMe:
								return !isEmirateCountry(data.principalDetails.nationalityId);
							case PolicyType.MeAndFamily:
								return !(
									isEmirateCountry(data.principalDetails.nationalityId) ||
									data.dependents?.some((dependent) => isEmirateCountry(dependent.nationalityId))
								);
						}

						return true;
					},
					{
						message: EmptyValidationMessage,
					}
				),
				planCategoryId: z
					.number()
					.optional()
					.refine(
						(planCategoryId) => {
							const { policyTypeId } = data;
							const salaryBandId =
								policyTypeId === PolicyType.DomesticWorker
									? data.domesticWorker?.salaryBandId
									: data.principalDetails?.salaryBandId;

							return !(isNil(planCategoryId) && salaryBandId === SalaryBands.BelowOrUpTo4000);
						},
						{
							message: EmptyValidationMessage,
						}
					),
			})
			.merge(getCommonValidations())
			.merge(getPrincipalValidations({ formValues: data }));

	const getOnlyMeValidation = () => z.object({}).merge(getBaseValidation());

	const getFamilyValidation = (data: IEidMembersForm) =>
		z
			.object({})
			.merge(getBaseValidation())
			.merge(getDependentsValidations({ formValues: data, maximumNoOfMember }));

	const getDomesticWorkerValidation = () =>
		z
			.object({})
			.merge(getBaseValidation())
			.merge(getDomesticWorkerValidations({ formValues: data }));

	const getValidations = (data: IEidMembersForm) => {
		const policyTypeId = data.policyTypeId;

		switch (policyTypeId) {
			case PolicyType.DomesticWorker:
				return getDomesticWorkerValidation();
			case PolicyType.MeAndFamily:
				return getFamilyValidation(data);
			case PolicyType.OnlyFamily:
				return getFamilyValidation(data);
			default:
				return getOnlyMeValidation();
		}
	};

	return getValidations(data);
};
